import { takeEvery, take, select, put, all, call } from 'redux-saga/effects';
import { callFinishActions, callFinishByRemoteActions } from './actions';
import videoSelectors from './selectors';
import { sendEndCall, sendRejectCall, sendCancelCall } from '../communication/actions';
import authSelectors from '../auth/selectors';
import authActions from '../auth/actions';
import communicationSelectors from '../communication/selectors';
import { getLogger } from '../../logger/index';
import { closeRemoteStream, closeConnection } from './kvsWebRTC';

const logger = getLogger('communicationVideo :: endCall');

function* endCallByLocalSaga(action) {
  try {
    yield call(endRemoteCallSaga, action);

    logger.debug('Close stream by action', action);
    const remoteCall = yield select(videoSelectors.getRemoteCallByUUID(action.payload.uuid));
    if (remoteCall && remoteCall.stream) {
      closeRemoteStream(remoteCall.stream);
    } else {
      logger.debug('No remote stream found for uuid', action.payload.uuid);
    }

    const hasPendingCalls = yield select(videoSelectors.hasPendingCalls);
    if (!hasPendingCalls) {
      const localStream = yield select(videoSelectors.getLocalStream);
      closeConnection(localStream);
    }
  } catch (e) {
    logger.error(e);
  } finally {
    logger.debug('Local call ended', { forceSend: true });
  }
}

function* endCallByRemoteSaga({ payload: { from } }) {
  try {
    logger.debug('Close stream by action', { payload: { from } });
    const remoteCall = yield select(videoSelectors.getRemoteCall(from.id));
    if (remoteCall && remoteCall.stream) {
      logger.debug('Close remote stream on end call by remote action');
      closeRemoteStream(remoteCall.stream);
    }

    const hasPendingCalls = yield select(videoSelectors.hasPendingCalls);
    if (!hasPendingCalls) {
      const localStream = yield select(videoSelectors.getLocalStream);
      closeConnection(localStream);
    }
  } catch (e) {
    logger.error(e);
  } finally {
    logger.debug('Remote call ended', { forceSend: true });
  }
}

function* endRemoteCallSaga({ payload: { uuid } }) {
  try {
    const isAnonymous = yield select(communicationSelectors.isAnonymous);
    let toId;
    let toName;
    let isEmployee = false;
    if (isAnonymous === true) {
      const anonymous = yield select(communicationSelectors.getAnonymous);
      toId = anonymous.email;
      toName = anonymous.name;
    } else {
      const auth = yield select(authSelectors.getAuth);
      if (!auth) {
        yield take([authActions.loginSuccess, authActions.authSuccess]);
      }
      toId = yield select(authSelectors.getId);
      toName = yield select(authSelectors.getName);
      isEmployee = yield select(authSelectors.isEmployee);
    }

    const isIncommingCall = yield select(videoSelectors.isIncommingCall);
    const isLocalAccepted = yield select(videoSelectors.isCallAcceptedByLocal);

    if (uuid) {
      const remote = yield select(videoSelectors.getRemoteCallByUUID(uuid));
      logger.debug('Close remote call on end call by local action');
      yield endRemoteCall(remote, { isLocalAccepted, isIncommingCall, toId, toName, isEmployee });
    } else {
      const remoteCalls = yield select(videoSelectors.getRemoteCalls);

      logger.warn('No UUID deliverer in the endCall action');
      logger.debug('Close endCall event to all remotes that are disconnected');
      yield all(
        remoteCalls.map(remote => endRemoteCall(remote, { isLocalAccepted, isIncommingCall, toId, toName, isEmployee }))
      );
    }
  } catch (e) {
    logger.error(e);
  }
}

function endRemoteCall(
  { isAccepted, isCalling, type, user },
  { isLocalAccepted, isIncommingCall, toId, toName, isEmployee }
) {
  const socketAction = isAccepted || isLocalAccepted ? sendEndCall : isCalling ? sendRejectCall : sendCancelCall;

  const socketArgs = isIncommingCall
    ? {
        to: toId,
        toName: toName,
        from: user.id,
        fromName: user.name,
        role: user.role,
        customerVideoCall: user.customerVideoCall,
      }
    : {
        to: user.id,
        toName: user.name,
        from: toId,
        fromName: toName,
        role: isEmployee ? 'employee' : 'customer',
        customerVideoCall: user.customerVideoCall,
      };
  return put(socketAction(type === 'video', socketArgs));
}

// function* socketDisconnectSaga({ payload }) {
//   try {
//     const userId = payload.args;
//     const remote = yield select(videoSelectors.getActiveRemoteCall(userId));

//     if (remote) {
//       yield put(endCallByRemote(remote.user));
//     }
//   } catch (e) {
//     logger.error(e);
//   }
// }

export default function* watchEndCallSaga() {
  try {
    logger.debug('Watch end call actions');
    yield takeEvery(callFinishActions, endCallByLocalSaga);
    yield takeEvery(callFinishByRemoteActions, endCallByRemoteSaga);

    // it should be uncommented when the events stores the socketId
    // yield takeEvery(
    //   ({ type, payload }) => type === 'SOCKET_EVENT' && payload.eventName === 'user_disconnected',
    //   socketDisconnectSaga
    // );
  } catch (e) {
    logger.error(e);
  }
}
